/* eslint-disable sort-keys-fix/sort-keys-fix */

/*  Shared  */
import { Colors } from '@auviknetworks/iceberg-ui';
import { FontSizes, SIDEBAR_WIDTH } from 'application/Shared/Constants';

import { OwnerState } from './muiTypes';

const {
    auvikBrown,
    auvikCyan,
    auvikEggplant,
    auvikGreen,
    auvikLavender,
    auvikLime,
    auvikOrange,
    auvikPink,
    auvikPurple,
    auvikRed,
    auvikSkyBlue,
    auvikYellow,
    black,
    eggplantGray,
    gray,
    lavenderGray,
    lightGray,
    white
} = Colors;

/*  Styles  */
export const defaultLightTheme2023 = {
    palette: {
        accountsReport: {
            chipType: {
                dormant: {
                    backgroundColor: auvikRed[50],
                    borderColor: auvikRed[500],
                    textColor: auvikRed[900]
                },
                active: {
                    backgroundColor: auvikGreen[100],
                    borderColor: auvikGreen[900],
                    textColor: auvikGreen[900]
                }
            }
        },
        alert: {
            text: {
                info: auvikCyan[800],
                success: auvikGreen[900],
                warning: auvikYellow[900],
                error: auvikRed[800]
            }
        },
        anmTrial: {
            backgroundColorPrimary: white,
            backgroundColorSecondary: eggplantGray[50],
            errorScreen: {
                backgroundColor: auvikYellow[50],
                borderColor: auvikYellow[100],
                textColor: auvikYellow[900]
            },
            linkColor: auvikPurple[500],
            primaryTextColor: gray[700],
            titleTextColor: auvikEggplant[500]
        },
        avatar: {
            appsAvatar: { backgroundColor: white },
            appsAvatarGroup: { backgroundColor: white },
            sso: { backgroundColor: auvikPurple[50], color: auvikPurple[800] },
            ssoSecondary: {
                backgroundColor: auvikGreen[50]
            },
            text: { white, black }
        },
        background: {
            avatar: gray[50],
            calendarSelectedSection: auvikPurple[50],
            configureIntegration: white,
            contractSectionTitle: white,
            contractsReport: white,
            contractSummaryCardWhite: white,
            contractTable: lightGray[200],
            contractTableLight: white,
            danger: auvikRed[500],
            integrationMethodIcon: lavenderGray[50],
            login: white,
            loginGraphic: auvikEggplant[500],
            optionIntegration: eggplantGray[50],
            pageNotFound: auvikPurple[500],
            primary: eggplantGray[50],
            primaryLight: eggplantGray[50],
            reports: white,
            reportSummaryCardDark: auvikLavender[800],
            reportSummaryCardLight: auvikLavender[50],
            saasOpsUserDetails: white,
            secondary: lavenderGray[50],
            tertiary: white,
            timePeriodPicker: white,
            warning: auvikOrange[500],
            white
        },
        bigCalendar: {
            monthly: {
                background: auvikGreen[50],
                border: auvikGreen[500],
                icon: auvikGreen[800]
            },
            renew: {
                background: lavenderGray[100],
                border: lavenderGray[900],
                icon: gray[900]
            },
            agendaTableBorderColor: gray[50],
            allDayCellBorderColor: gray[50],
            appName: gray[700],
            backgroundEvent: white,
            backgroundEventFocusOutline: auvikLavender[800],
            backgroundEventSelectedBackground: auvikLavender[800],
            buttonLink: eggplantGray[800],
            buttonLinkHover: auvikLavender[900],
            currentTimeIndicatorBackgroundColor: white,
            dayBgBorderColor: lavenderGray[500],
            daySlotBackgroundEventBorderColor: auvikLavender[900],
            headerBackgroundColor: white,
            headerColor: eggplantGray[500],
            monthRowBorderColor: lavenderGray[500],
            monthViewBorderColor: lavenderGray[500],
            offRange: gray[100],
            overflowingBorderColor: gray[50],
            overlayBackgroundColor: white,
            overlayBorderColor: lightGray[500],
            overlayHeaderBorderColor: lightGray[500],
            timeContentBorderColor: gray[50],
            timeHeaderBorderColor: gray[50],
            timeHeaderGutterBackgroundColor: white,
            timeHeaderGutterBorderColor: gray[50],
            timeSlotBorderColor: lavenderGray[50],
            timeSlotGroupBorderColor: gray[50],
            timeViewBorderColor: gray[50],
            todayBackgroundColor: auvikLavender[50],
            toolbarButtonActiveBackgroundColor: gray[50],
            toolbarButtonActiveBorderColor: gray[200],
            toolbarButtonActiveHoverBackgroundColor: gray[100],
            toolbarButtonActiveHoverBorderColor: gray[300],
            toolbarButtonActiveHoverColor: gray[700],
            toolbarButtonBorderColor: gray[100],
            toolbarButtonColor: gray[700],
            toolbarButtonHoverBackgroundColor: gray[50],
            toolbarButtonHoverBorderColor: gray[200],
            toolbarButtonHoverColor: gray[700],
            toolbarLabel: eggplantGray[800]
        },
        border: {
            appBubbles: lightGray[500],
            card: lavenderGray[500],
            divider: lavenderGray[400],
            darkerDivider: lavenderGray[500],
            pageHeader: lavenderGray[500],
            pageNotFound: white,
            primary: gray[500],
            secondary: auvikEggplant[500],
            tertiary: auvikPurple[500],
            highlightedCard: auvikPurple[500],
            userDetailsAppHeadlineCard: lavenderGray[500]
        },
        breach: {
            backgroundColor: { safe: auvikGreen[50], critical: auvikRed[50] },
            borderColor: {
                safe: auvikGreen[500],
                critical: auvikRed[900]
            },
            textColor: {
                default: gray[700],
                safe: auvikGreen[500],
                critical: auvikRed[900]
            }
        },
        button: {
            primary: auvikLavender[800],
            cancel: auvikRed[500],
            danger: auvikRed[500],
            success: auvikGreen[500],
            warning: auvikRed[500],
            white
        },
        calendar: { monthly: auvikGreen[800], renew: gray[900] },
        chart: {
            axis: lavenderGray[500],
            axisLine: gray[500],
            axisTickLine: gray[500],
            axisTickValue: gray[500],
            bar: auvikPurple[500],
            line: auvikEggplant[500],
            saasHealthLineGraph: gray[500],
            tooltipBackground: white,
            tooltipObjectBackground: lavenderGray[400],
            primary: auvikLavender[500],
            secondary: auvikPurple[500],
            tertiary: auvikCyan[500],
            danger: auvikRed[500],
            success: auvikGreen[500],
            warning: auvikOrange[500]
        },
        checkbox: {
            border: { primary: lavenderGray[700] },
            checked: {
                primary: auvikEggplant[500],
                secondary: auvikPurple[500],
                tertiary: auvikLavender[800]
            }
        },
        chip: {
            background: {
                primary: lightGray[300],
                secondary: lavenderGray[400],
                tertiary: auvikPurple[500]
            },
            text: {
                primary: gray[700]
            }
        },
        community: {
            likeBadge: {
                likedBackgroundColor: auvikLavender[800],
                unlikedBackgroundColor: white,
                borderColor: auvikLavender[800]
            }
        },
        confetti: {
            color1: '#F1EAF3',
            color2: '#DBCAE2',
            color3: auvikEggplant[500],
            color4: '#6A3583',
            color5: '#572572',
            color6: '#BEBDD0',
            color7: auvikPurple[500],
            color8: '#171446'
        },
        contract: {
            chipType: {
                month: {
                    backgroundColor: auvikGreen[50],
                    borderColor: auvikGreen[500],
                    iconColor: auvikGreen[500]
                },
                renew: {
                    backgroundColor: lavenderGray[100],
                    borderColor: lavenderGray[900],
                    iconColor: lavenderGray[900]
                }
            }
        },
        customizedDot: { color: white, border: gray[500] },
        danger: { main: auvikRed[500] },
        error: { main: auvikRed[500] },
        externalAppStatus: {
            critical: auvikRed[500],
            major: auvikOrange[500],
            minor: auvikYellow[800],
            none: auvikGreen[800],
            resolved: auvikGreen[800]
        },
        fileBrowser: {
            borderColor: gray[50],
            linkColor: auvikPurple[500],
            borderHighlight: eggplantGray[500],
            itemColor: white,
            itemBackgroundColor: gray[100],
            tableHover: auvikPurple[500],
            tableLink: gray[700],
            fileBrowser: lavenderGray[100],
            fileBrowserSelected: gray[100]
        },
        graph: {
            axisLabels: gray[700],
            colors: [
                auvikEggplant[500],
                auvikLavender[500],
                auvikYellow[500],
                auvikGreen[500],
                auvikOrange[500],
                auvikPurple[500],
                auvikLime[900],
                auvikPink[900],
                auvikSkyBlue[500],
                auvikCyan[500]
            ]
        },
        hover: {
            primary: auvikLavender[900],
            secondary: auvikEggplant[500],
            tertiary: auvikPurple[500]
        },
        iceberg: {
            colorAction: auvikLavender[800],
            colorBrandPrimary: auvikPurple[500],
            colorBrandSecondary: auvikEggplant[500]
        },
        icebergForm: {
            formLabelColor: gray[700],
            formLabelColorError: auvikRed[500],
            textFieldInputBackgroundColorReadOnly: lightGray[300],
            textFieldInputBackgroundColor: white,
            textFieldInputColor: gray[700],
            textFieldInputColorReadOnly: gray[300],
            textFieldOutline: lavenderGray[500],
            textFieldOutlineError: auvikRed[500],
            textFieldOutlineFocused: auvikSkyBlue[500],
            selectBackgroundColor: white,
            selectOutline: auvikSkyBlue[500],
            chevronIcon: lavenderGray[500]
        },
        icon: {
            acceptSave: auvikGreen[500],
            accordionExpand: auvikEggplant[500],
            accordionItem: auvikEggplant[500],
            avatar: auvikEggplant[500],
            badgeWarning: auvikYellow[500],
            cancel: auvikRed[500],
            clientDefault: auvikLavender[500],
            danger: auvikRed[500],
            disabled: eggplantGray[400],
            discoveredByQuickScan: auvikGreen[500],
            error: auvikRed[500],
            info: auvikCyan[800],
            infoDark: auvikSkyBlue[800],
            managedByPartner: auvikLavender[500],
            primary: auvikEggplant[500],
            search: lavenderGray[700],
            success: auvikGreen[500],
            tertiary: auvikPurple[500],
            textColor: gray[700],
            warning: auvikOrange[500],
            white
        },
        info: { main: auvikCyan[800] },
        lifecycleStages: {
            background: {
                unapproved: auvikRed[50],
                notConfigured: `${gray[300]}33`,
                evaluating: `${auvikBrown[500]}33`,
                implementing: `${auvikLime[500]}33`,
                approved: auvikGreen[50],
                decommissioning: lavenderGray[100],
                decommissioned: `${auvikCyan[100]}33`
            },
            borderColor: {
                unapproved: auvikRed[900],
                notConfigured: gray[300],
                evaluating: auvikBrown[500],
                implementing: auvikLime[500],
                approved: auvikGreen[500],
                decommissioning: lavenderGray[900],
                decommissioned: auvikCyan[100]
            },
            icon: {
                unapproved: auvikRed[900],
                notConfigured: gray[300],
                evaluating: auvikBrown[500],
                implementing: auvikLime[500],
                approved: auvikGreen[500],
                decommissioning: lavenderGray[900],
                decommissioned: auvikCyan[900]
            },
            iconFilter: {
                unapproved: auvikRed[900],
                notConfigured: gray[300],
                evaluating: auvikBrown[500],
                implementing: auvikLime[500],
                approved: auvikGreen[500],
                decommissioning: lavenderGray[900],
                decommissioned: auvikCyan[900]
            },
            textColor: {
                unapproved: gray[900],
                notConfigured: gray[900],
                evaluating: gray[900],
                implementing: gray[900],
                approved: gray[900],
                decommissioning: gray[900],
                decommissioned: gray[900]
            }
        },
        managedPicker: {
            background: {
                managed: auvikGreen[50],
                unmanaged: `${gray[300]}33`,
                unclassified: auvikRed[50]
            },
            borderColor: {
                managed: auvikGreen[500],
                unmanaged: gray[300],
                unclassified: auvikRed[900]
            },
            icon: {
                managed: auvikGreen[500],
                unmanaged: gray[300],
                unclassified: auvikRed[900]
            },
            iconFilter: {
                managed: auvikGreen[500],
                unmanaged: gray[300],
                unclassified: auvikRed[900]
            },
            textColor: {
                managed: gray[900],
                unmanaged: gray[900],
                unclassified: gray[900]
            }
        },
        menuItem: {
            hoverBackgroundColor: auvikLavender[50]
        },
        metadata: { background: gray[800] },
        modal: {
            backgroundColor: {
                footer: lightGray[200],
                title: auvikEggplant[500]
            },
            text: {
                titleTextPrimary: white
            },
            card: {
                backgroundColor: white
            }
        },
        notes: {
            icon: {
                client: gray[500],
                community: auvikLavender[800],
                open: auvikLavender[800],
                partner: auvikLavender[500]
            }
        },
        onboarding: {
            backgroundColorPrimary: white,
            backgroundColorSecondary: lavenderGray[200],
            backgroundColorGray: lavenderGray[200],
            backgroundColorWhite: white,
            bonusConfigurationBadgeBackgroundColor: auvikLavender[900],
            buttonPrimary: auvikLavender[800],
            cardBodyText: auvikEggplant[500],
            checklistBackgroundColor: white,
            deployAgentInsteadGraphic: {
                backgroundColor: {
                    light: lavenderGray[100],
                    medium: lavenderGray[300],
                    dark: lavenderGray[500]
                }
            },
            integrationCardBackgroundColor: white,
            integrationCardBorderColor: lavenderGray[500],
            primaryTextColor: gray[700],
            quickScanChooseCardBackgroundColor: lavenderGray[200],
            tertiaryIconColor: auvikPurple[500],
            tertiaryTextColor: auvikPurple[500],
            titleTextColor: auvikEggplant[500]
        },
        pieChart: {
            personal: auvikYellow[500],
            domain: auvikPurple[900],
            na: auvikLavender[100]
        },
        primary: { main: auvikEggplant[500] },
        progressBar: {
            backgroundColor: lightGray[200],
            main: auvikLavender[800]
        },
        providerGroup: {
            default: {
                borderColor: auvikSkyBlue[900],
                backgroundColor: auvikSkyBlue[100],
                text: gray[900]
            },
            orgUnit: {
                borderColor: auvikPurple[500],
                backgroundColor: auvikPurple[50],
                text: gray[900]
            }
        },
        qbrFirstReports: {
            AppClassificationScore: auvikEggplant[500],
            ManagedAppScore: auvikCyan[800],
            ShadowITScore: auvikYellow[500],
            UnapprovedAppScore: auvikOrange[500],
            AppOverlapScore: auvikPurple[500],
            SaaSSophisticationScore: auvikLavender[800],
            scoreTextColor: {
                increase: auvikGreen[500],
                decrease: auvikRed[500],
                unchanged: gray[700]
            }
        },
        reports: {
            v2Header: {
                boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.15)'
            }
        },
        risk: {
            backgroundColor: {
                critical: `${auvikRed[500]}33`,
                high: `${auvikOrange[500]}33`,
                medium: `${auvikYellow[500]}33`,
                low: auvikGreen[50],
                alert: `${auvikPurple[800]}33`,
                safe: `${auvikCyan[500]}33`,
                undefined: gray[100]
            },
            borderColor: {
                critical: auvikRed[500],
                high: auvikOrange[500],
                medium: auvikYellow[500],
                low: auvikGreen[500],
                alert: auvikPurple[800],
                safe: auvikCyan[500],
                undefined: gray[800]
            },
            icon: {
                critical: auvikRed[500],
                high: auvikOrange[500],
                medium: auvikYellow[500],
                low: auvikGreen[500],
                alert: auvikPurple[800],
                safe: auvikCyan[900],
                undefined: gray[800]
            },
            iconFilter: {
                critical: auvikRed[500],
                high: auvikOrange[500],
                medium: auvikYellow[500],
                low: auvikGreen[500],
                alert: auvikPurple[800],
                safe: auvikCyan[900],
                undefined: gray[800]
            },
            pieChartColor: {
                critical: auvikRed[500],
                high: auvikOrange[500],
                medium: auvikYellow[500],
                low: auvikGreen[500],
                default: auvikEggplant[500]
            },
            textColor: {
                critical: gray[900],
                high: gray[900],
                medium: gray[900],
                low: gray[900],
                alert: gray[900],
                safe: gray[900],
                undefined: gray[900]
            }
        },
        saasScore: {
            borderColor: {
                excellent: auvikGreen[900],
                medium: auvikOrange[900],
                low: auvikRed[800],
                none: black
            },
            fillColor: {
                excellent: auvikGreen[500],
                medium: auvikOrange[500],
                low: auvikRed[500],
                none: gray[500]
            },
            unfilledColor: {
                excellent: auvikGreen[50],
                medium: auvikOrange[50],
                low: auvikRed[50],
                none: gray[50]
            },
            text: {
                excellent: auvikGreen[500]
            }
        },
        saasOpsAppList: {
            backgroundColor: {
                hover: auvikLavender[50],
                current: auvikLavender[50]
            },
            borderBottom: lavenderGray[500],
            trash: {
                backgroundColor: gray[100]
            }
        },
        saasOpsCard: {
            backgroundColor: white
        },
        saasOpsState: {
            background: {
                integrated: auvikGreen[50],
                error: auvikRed[50]
            },
            borderColor: {
                integrated: auvikGreen[500],
                error: auvikRed[500]
            },
            icon: {
                integrated: auvikGreen[500],
                error: auvikRed[500]
            },
            textColor: {
                integrated: gray[900]
            }
        },
        scripts: {
            color: black,
            backgroundColor: auvikGreen[50],
            borderColor: auvikGreen[50]
        },
        sidebar: {
            backgroundColor: auvikEggplant[500],
            backgroundColorBottomSection: auvikEggplant[900],
            clientTitle: white,
            clientName: white,
            divider: auvikEggplant[500],
            text: white,
            icon: white,
            itemSelected: {
                backgroundColor: auvikPurple[500],
                hover: {
                    backgroundColor: auvikPurple[500]
                }
            },
            item: {
                hover: {
                    backgroundColor: auvikEggplant[900]
                },
                bottomItemHover: {
                    backgroundColor: auvikEggplant[500]
                }
            },
            newBadge: {
                selected: auvikEggplant[500],
                unselected: auvikPurple[500]
            },
            mainTabSelected: {
                backgroundColor: auvikPurple[500],
                hover: {
                    backgroundColor: auvikPurple[500]
                }
            },
            mainTab: {
                hover: {
                    backgroundColor: auvikEggplant[900]
                },
                color: white
            },
            subTabSelected: {
                borderLeftColor: white,
                hover: {
                    backgroundColor: auvikEggplant[500]
                }
            },
            subTab: {
                hover: {
                    backgroundColor: auvikEggplant[900]
                },
                color: lavenderGray[100]
            }
        },
        secondary: { main: auvikLavender[500] },
        sparkLines: {
            sparkLine: auvikLavender[500],
            sparkBar: auvikLavender[500],
            stroke: white
        },
        ssoSupport: {
            globalSupport: {
                iconPrint: eggplantGray[500],
                background: lavenderGray[600]
            },
            tenantUse: {
                iconPrint: auvikGreen[500],
                background: auvikGreen[50]
            },
            tooltip: {
                divider: lavenderGray[500]
            }
        },
        success: { main: auvikGreen[500] },
        switchAuvik: {
            baseChecked: white,
            baseTrackBackground: auvikPurple[500],
            baseFocusBorder: white,
            baseFocusColor: auvikPurple[500],
            baseDisabled: lavenderGray[700],
            thumbBackground: white,
            thumbColor: white,
            trackBackground: lightGray[900]
        },
        tab: { divider: white, subdivider: lavenderGray[500] },
        table: {
            border: lavenderGray[500],
            dangerRowBackground: auvikRed[50],
            dangerRowText: auvikRed[800],
            evenRowBackground: lavenderGray[50],
            emptyRowsBackground: white,
            footerBackground: white,
            headerRowBackground: lightGray[300],
            headerBorderSortable: auvikPurple[500],
            oddRowBackground: white,
            rowBackground: white,
            subtableBackground: lightGray[100],
            subtableHeader: white,
            successRowBackground: auvikGreen[50],
            successRowText: auvikGreen[800],
            totalRowBackground: gray[50]
        },
        tag: {
            option1: auvikCyan[500],
            option2: auvikLavender[500],
            option3: auvikOrange[500],
            option4: auvikGreen[500],
            option5: auvikBrown[500],
            option6: auvikPink[500],
            option7: auvikYellow[500],
            option8: auvikSkyBlue[500]
        },
        tenantSelector: {
            input: {
                backgroundColor: auvikEggplant[100],
                borderColor: auvikEggplant[100]
            },
            menuListColor: white,
            paperProps: {
                item: {
                    color: white,
                    hover: {
                        backgroundColor: auvikEggplant[800],
                        color: white
                    }
                },
                itemSelected: {
                    backgroundColor: auvikPurple[500],
                    color: white,
                    hover: {
                        backgroundColor: auvikPurple[500]
                    }
                },
                itemSelectedHover: {
                    backgroundColor: auvikPurple[500],
                    color: white
                },
                backgroundColor: auvikEggplant[100],
                borderColor: auvikEggplant[100]
            }
        },
        tertiary: { main: auvikPurple[500] },
        text: {
            calendarSelectedSection: auvikPurple[800],
            calendarSelectedSectionDisabled: auvikPurple[800],
            cardKPI: auvikPurple[500],
            cardKPINoData: auvikEggplant[500],
            danger: auvikRed[800],
            disabled: eggplantGray[400],
            emailAddress: auvikLavender[500],
            error: auvikRed[800],
            link: { text: auvikPurple[500], hover: auvikPurple[800] },
            noData: auvikEggplant[500],
            overlappingAppsCount: auvikPurple[500],
            pageNotFound: white,
            primary: gray[700],
            primaryFaded: gray[400],
            reportSummaryCardDark: auvikLavender[900],
            reportSummaryCardLight: white,
            secondary: auvikEggplant[500],
            success: auvikGreen[900],
            tag: { primary: gray[700] },
            tertiary: auvikPurple[500],
            warning: auvikBrown[500],
            white
        },
        toastColors: {
            red: {
                background: Colors.auvikRed[50],
                border: Colors.auvikRed[900],
                icon: Colors.auvikRed[900],
                progressBar: Colors.auvikRed[900],
                text: Colors.auvikRed[800]
            },
            blue: {
                background: Colors.auvikCyan[50],
                border: Colors.auvikCyan[900],
                icon: Colors.auvikCyan[900],
                progressBar: Colors.auvikCyan[900],
                text: Colors.auvikCyan[900]
            },
            green: {
                background: Colors.auvikGreen[50],
                border: Colors.auvikGreen[900],
                icon: Colors.auvikGreen[900],
                progressBar: Colors.auvikGreen[900],
                text: Colors.auvikGreen[900]
            },
            yellow: {
                background: Colors.auvikYellow[50],
                border: Colors.auvikYellow[900],
                icon: Colors.auvikYellow[900],
                progressBar: Colors.auvikYellow[900],
                text: Colors.auvikBrown[900]
            }
        },
        tooltips: {
            darkTooltip: {
                background: black,
                color: white,
                arrowColor: black
            },
            lightTooltip: {
                background: white,
                border: lavenderGray[500],
                color: gray[700],
                arrowColor: white
            },
            pieChart: {
                backgroundColor: white,
                headerRowBackgroundColor: eggplantGray[50]
            },
            tooltipQ: {
                color: gray[500]
            }
        },
        topInfoBanner: {
            defaultBackgroundColor: white,
            defaultBorderColor: eggplantGray[500],
            configureIntegration: {
                backgroundColor: auvikCyan[50],
                borderColor: auvikCyan[100],
                textColor: auvikCyan[800],
                titleColor: auvikCyan[800]
            }
        },
        trialExpiryJailCard: {
            backgroundTrialJailGradientEnd: auvikLavender[900],
            backgroundTrialJailGradientStart: auvikEggplant[900],
            cardBackgroundColor: white,
            divider: auvikEggplant[500],
            icon: auvikEggplant[500],
            logoutIcon: white,
            logoutText: white,
            subcardBorderColor: auvikEggplant[500],
            subtitle: auvikEggplant[500],
            text: gray[700],
            title: auvikEggplant[500]
        },
        usage: {
            badgeIcon: {
                high: auvikGreen[900],
                low: auvikBrown[500],
                medium: auvikGreen[500],
                waste: auvikRed[900]
            },
            badgeTextColor: {
                high: auvikGreen[900],
                low: auvikBrown[500],
                medium: auvikGreen[500],
                waste: auvikRed[900]
            },
            icon: {
                high: auvikGreen[900],
                low: auvikBrown[500],
                medium: auvikGreen[500],
                waste: auvikRed[900]
            },
            textColor: {
                high: auvikGreen[900],
                low: auvikBrown[500],
                medium: auvikGreen[500],
                waste: auvikRed[900]
            },
            barChart: {
                'High Usage': `${auvikGreen[500]}33`,
                'Medium Usage': `${auvikLime[500]}33`,
                'Low Usage': `${auvikYellow[500]}33`,
                Waste: `${auvikRed[500]}33`
            }
        },
        userInvitationStatus: {
            accepted: {
                icon: auvikGreen[500],
                background: auvikGreen[50],
                borderColor: auvikGreen[500],
                text: gray[700]
            },
            pending: {
                icon: auvikYellow[500],
                background: auvikYellow[50],
                borderColor: auvikYellow[500],
                text: gray[700]
            }
        },
        viewPricing: {
            dividerPerUser: auvikPurple[500],
            dividerExclusive: auvikEggplant[500],
            icon: auvikPurple[500],
            textTitle: auvikEggplant[500],
            textSubtitle: gray[700],
            textPerUser: auvikPurple[500],
            textExclusive: auvikEggplant[500]
        },
        warning: { main: auvikBrown[500] }
    },
    typography: {
        fontFamily: [
            '"Open Sans"',
            'Helvetica',
            'Arial',
            'Verdana',
            'sans-serif'
        ].join(','),
        fontSize: FontSizes[1],
        h1: {
            color: auvikEggplant[500],
            fontSize: FontSizes[5],
            fontWeight: 500,
            padding: 0,
            textTransform: 'capitalize'
        },
        h2: {
            color: auvikEggplant[500],
            fontSize: FontSizes[4],
            fontWeight: 500,
            padding: 0,
            textTransform: 'capitalize'
        },
        h3: {
            color: auvikEggplant[500],
            fontSize: FontSizes[4],
            fontWeight: 'normal',
            padding: 0,
            marginTop: '4px',
            marginBottom: '8px',
            textTransform: 'capitalize'
        },
        h4: {
            color: auvikEggplant[500],
            fontSize: FontSizes[3],
            textTransform: 'capitalize'
        },
        h5: {
            color: auvikEggplant[500],
            fontSize: FontSizes[3],
            textTransform: 'capitalize'
        },
        h6: {
            color: auvikEggplant[500],
            fontSize: FontSizes[2],
            textTransform: 'capitalize'
        },
        subtitle1: {
            color: gray[700],
            fontSize: FontSizes[2],
            textTransform: 'capitalize'
        },
        subtitle2: {
            color: gray[700],
            fontSize: FontSizes[2]
        },
        body1: {
            fontSize: FontSizes[1]
        },
        body2: {
            color: gray[700],
            fontSize: FontSizes[1]
        },
        button: {
            fontSize: FontSizes[1],
            textTransform: 'uppercase'
        },
        codeBlock: {
            color: black,
            fontFamily: '"Courier New", Courier, monospace',
            fontWeight: 600,
            whiteSpace: 'pre-line'
        },
        small: {
            fontSize: '11px'
        },
        span: {
            fontSize: FontSizes[1]
        }
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                a: {
                    color: gray[700],
                    cursor: 'pointer',
                    textDecoration: 'none !important',
                    '&.link.underline': {
                        borderBottom: '1px solid transparent',
                        color: gray[700],
                        '&:hover': {
                            color: auvikPurple[500],
                            borderBottom: `1px solid ${auvikPurple[500]}`
                        }
                    },
                    '&.bold.link.underline': {
                        borderBottom: '1px solid transparent',
                        fontWeight: 600,
                        color: gray[700],
                        '&:hover': {
                            color: auvikPurple[500],
                            borderBottom: `1px solid ${auvikPurple[500]}`
                        }
                    },
                    '&.link': {
                        color: gray[700],
                        '&:hover': {
                            color: auvikPurple[500]
                        }
                    },
                    '&.link--secondary': {
                        color: auvikLavender[500],
                        '&:hover': {
                            color: auvikPurple[500]
                        }
                    },
                    '&.link--tertiary': {
                        color: auvikPurple[500],
                        '&:hover': {
                            color: auvikPurple[500]
                        }
                    },
                    '&.link--white': {
                        '&:hover': {
                            color: white
                        }
                    },
                    '&.link--errorAlert': {
                        borderBottom: '1px solid transparent',
                        color: auvikRed[800],
                        '&:hover': {
                            color: auvikPurple[500],
                            borderBottom: `1px solid ${auvikPurple[500]}`
                        }
                    },
                    '&.link--infoAlert': {
                        borderBottom: '1px solid transparent',
                        color: auvikCyan[800],
                        '&:hover': {
                            color: auvikPurple[500],
                            borderBottom: `1px solid ${auvikPurple[500]}`
                        }
                    },
                    '&.showLink': {
                        '@media print': {
                            color: auvikEggplant[500],
                            textDecoration: 'underline !important'
                        }
                    }
                },
                code: {
                    fontFamily: "'Courier New', Courier, monospace !important",
                    fontWeight: 600,
                    backgroundColor: lightGray[100],
                    color: black,
                    padding: '4px',
                    borderRadius: '4px',
                    fontSize: '13px'
                },
                html: {
                    height: '100%'
                },
                input: {
                    '&.link': {
                        color: gray[700],
                        cursor: 'pointer',
                        fontWeight: 600,
                        '&:hover': {
                            color: auvikPurple[500]
                        }
                    }
                },
                li: {
                    '&.li-with-spacing': {
                        lineHeight: '24px',
                        marginBottom: '4px',
                        marginTop: '4px'
                    },
                    '&.li-purple': {
                        color: auvikEggplant[100],
                        display: 'list-item',
                        listStyleType: 'disc',
                        paddingLeft: '0px',
                        '&::marker': {
                            color: auvikPurple[500],
                            fontSize: '16px'
                        }
                    },
                    // Markdown styles
                    '&.markdown-li': {
                        lineHeight: '24px',
                        marginBottom: '12px',
                        '> p': { marginBottom: 0 },
                        '&::marker': {
                            color: auvikPurple[500],
                            fontSize: '20px'
                        },
                        '& > ul > li': {
                            listStyleType: 'disc',
                            marginBottom: 0,
                            '&::marker': {
                                color: auvikPurple[100],
                                fontSize: '20px'
                            }
                        }
                    }
                },
                ul: {
                    '&.markdown-ul': {
                        marginBottom: 0,
                        paddingBottom: '8px',
                        paddingLeft: '20px'
                    }
                },
                span: {
                    '&.link--errorAlert': {
                        borderBottom: '1px solid transparent',
                        color: auvikRed[800],
                        '&:hover': {
                            color: auvikPurple[500],
                            borderBottom: `1px solid ${auvikPurple[500]}`
                        }
                    },
                    '&.link': {
                        '&:hover': {
                            color: auvikPurple[500],
                            textDecoration: 'underline'
                        },
                        cursor: 'pointer'
                    }
                }
            }
        },
        // Name of the component
        MuiAccordion: {
            styleOverrides: {
                // Name of the slot
                root: {
                    // Some CSS
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    border: `1px solid ${lavenderGray[500]}`,
                    boxShadow: 'none'
                }
            }
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    borderTop: `1px solid ${lavenderGray[500]}`,
                    paddingTop: '20px'
                }
            }
        },
        MuiAlert: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'standard' &&
                        ownerState.severity === 'info' && {
                            backgroundColor: auvikCyan[50],
                            border: `1px solid ${auvikCyan[100]}`,
                            color: auvikCyan[800]
                        }),
                    ...(ownerState.variant === 'standard' &&
                        ownerState.severity === 'success' && {
                            backgroundColor: auvikGreen[50],
                            border: `1px solid ${auvikGreen[100]}`,
                            color: auvikGreen[900]
                        }),
                    ...(ownerState.variant === 'standard' &&
                        ownerState.severity === 'warning' && {
                            backgroundColor: auvikYellow[50],
                            border: `1px solid ${auvikYellow[100]}`,
                            color: auvikYellow[900]
                        }),
                    ...(ownerState.variant === 'outlined' && // TODO find out who
                        ownerState.severity === 'warning' && {
                            border: `1px solid ${auvikYellow[100]}`,
                            color: auvikYellow[900]
                        }),
                    ...(ownerState.variant === 'standard' &&
                        ownerState.severity === 'error' && {
                            backgroundColor: auvikRed[50],
                            border: `1px solid ${auvikRed[100]}`,
                            color: auvikRed[800]
                        }),
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    '&:focus': {
                        outline: 'none'
                    }
                }),
                icon: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.severity === 'info' && {
                        color: auvikCyan[900]
                    }),
                    ...(ownerState.severity === 'success' && {
                        color: auvikGreen[900]
                    }),
                    ...(ownerState.severity === 'warning' && {
                        color: auvikYellow[900]
                    }),
                    ...(ownerState.severity === 'error' && {
                        color: auvikRed[800]
                    }),
                    paddingLeft: '5px',
                    marginRight: '7px'
                }),
                message: ({ ownerState }: { ownerState: OwnerState }) => ({
                    alignSelf: 'center',
                    ...(ownerState.severity === 'info' && {
                        color: auvikCyan[800]
                    }),
                    ...(ownerState.severity === 'success' && {
                        color: auvikGreen[900]
                    }),
                    ...(ownerState.severity === 'warning' && {
                        color: auvikYellow[900]
                    }),
                    ...(ownerState.severity === 'error' && {
                        color: auvikRed[800]
                    }),
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    '&:focus': {
                        outline: 'none'
                    }
                }),
                action: {
                    alignItems: 'center',
                    paddingTop: '4px',
                    paddingBottom: '4px'
                }
            }
        },
        MuiAlertTitle: {
            styleOverrides: {
                root: {
                    fontWeight: 600
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                input: {
                    color: gray[700]
                },
                option: {
                    color: gray[700]
                },
                popper: {
                    width: 'unset'
                }
            }
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[0],
                    width: 'fit-content',
                    borderRadius: '90px',
                    height: '24px',
                    minWidth: '24px'
                }
            }
        },
        MuiAvatarGroup: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                avatar: {
                    backgroundColor: lightGray[500],
                    borderRadius: '24px',
                    color: auvikEggplant[500],
                    fontSize: FontSizes[0],
                    fontWeight: 600,
                    height: '24px',
                    marginLeft: 0,
                    minWidth: '24px',
                    paddingLeft: '2px',
                    paddingRight: '2px',
                    width: 'auto'
                }
            }
        },
        MuiBadge: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                badge: {
                    '&:not(.MuiBadge-dot, .MuiBadge-newDot)': {
                        minWidth: '16px',
                        width: '16px',
                        height: '16px'
                    },
                    zIndex: 0
                },
                dot: {
                    backgroundColor: auvikEggplant[500]
                },
                newDot: {
                    minWidth: '8px',
                    height: '8px',
                    marginTop: '4px',
                    padding: '0',
                    borderRadius: '4px',
                    backgroundColor: auvikYellow[500]
                },
                standard: {
                    backgroundColor: auvikEggplant[500]
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    textTransform: 'uppercase',
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    '&:focus': {
                        outline: 'none'
                    },
                    '&.MuiButton-contained': {
                        '&:disabled': {
                            cursor: 'not-allowed',
                            pointerEvents: 'all'
                        }
                    },
                    '&.MuiButton-containedPrimary': {
                        backgroundColor: auvikLavender[800],
                        border: `1px solid ${auvikLavender[800]}`,
                        color: white,
                        boxShadow: 'none',
                        textTransform: 'uppercase',
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            backgroundColor: lavenderGray[600],
                            border: `1px solid ${lavenderGray[600]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            backgroundColor: auvikLavender[900],
                            border: `1px solid ${auvikLavender[900]}`,
                            color: white
                        },

                        '&:hover': {
                            backgroundColor: auvikLavender[900],
                            border: `1px solid ${auvikLavender[900]}`,
                            color: white
                        },
                        ...(ownerState.size === 'slim' && {
                            fontSize: '12px',
                            padding: '2px 10px'
                        })
                    },
                    '&.MuiButton-containedSecondary': {
                        backgroundColor: gray[300],
                        border: `1px solid ${gray[300]}`,
                        color: white,
                        boxShadow: 'none',
                        textTransform: 'uppercase',
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            backgroundColor: lavenderGray[600],
                            border: `1px solid ${lavenderGray[600]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            backgroundColor: lavenderGray[700],
                            border: `1px solid ${lavenderGray[700]}`,
                            color: white
                        },

                        '&:hover': {
                            backgroundColor: lavenderGray[700],
                            border: `1px solid ${lavenderGray[700]}`,
                            color: white
                        }
                    },
                    '&.MuiButton-containedDanger': {
                        backgroundColor: auvikRed[500],
                        border: `1px solid ${auvikRed[500]}`,
                        color: white,
                        boxShadow: 'none',
                        textTransform: 'uppercase',
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            backgroundColor: lavenderGray[600],
                            border: `1px solid ${lavenderGray[600]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            backgroundColor: auvikRed[900],
                            border: `1px solid ${auvikRed[900]}`,
                            color: white
                        },

                        '&:hover': {
                            backgroundColor: auvikRed[900],
                            border: `1px solid ${auvikRed[900]}`,
                            color: white
                        }
                    },
                    '&.MuiButton-iconWithoutBorder': {
                        // Disabled state has to override hover state
                        backgroundColor: 'transparent',
                        borderRadius: '50%',
                        color: gray[100],
                        '&&:disabled': {
                            color: lavenderGray[500],
                            cursor: 'not-allowed'
                        },

                        '&:focus-visible': {
                            backgroundColor: 'rgba(0, 0, 0, 0.15)'
                        },

                        '&:hover': {
                            textDecoration: 'none',
                            color: white
                        }
                    },
                    '&.MuiButton-outlinedPrimary': {
                        color: auvikLavender[800],
                        border: `1px solid ${auvikLavender[800]}`,
                        boxShadow: 'none',
                        backgroundColor: white,
                        textTransform: 'uppercase',
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            color: lavenderGray[600],
                            border: `1px solid ${lavenderGray[600]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            border: `1px solid ${auvikLavender[900]}`,
                            color: auvikLavender[900]
                        },

                        '&:hover': {
                            border: `1px solid ${auvikLavender[900]}`,
                            color: auvikLavender[900]
                        },

                        ...(ownerState.size === 'slim' && {
                            fontSize: '12px',
                            padding: '2px 10px'
                        })
                    },
                    '&.MuiButton-outlinedTransparentPrimary': {
                        color: auvikLavender[800],
                        border: `1px solid ${auvikLavender[800]}`,
                        boxShadow: 'none',
                        backgroundColor: 'transparent',
                        textTransform: 'uppercase',
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            color: lavenderGray[600],
                            border: `1px solid ${lavenderGray[600]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            border: `1px solid ${auvikLavender[900]}`,
                            color: auvikLavender[900]
                        },

                        '&:hover': {
                            border: `1px solid ${auvikLavender[900]}`,
                            color: auvikLavender[900]
                        }
                    },
                    '&.MuiButton-outlinedSecondary': {
                        color: gray[300],
                        border: `1px solid ${gray[300]}`,
                        boxShadow: 'none',
                        textTransform: 'uppercase',
                        backgroundColor: white,
                        // Disabled state has to override hover state
                        '&&:disabled': {
                            border: `1px solid ${lavenderGray[600]}`,
                            cursor: 'not-allowed'
                        },

                        '&:focus': {
                            border: `1px solid ${lavenderGray[500]}`,
                            color: lavenderGray[500]
                        },

                        '&:hover': {
                            border: `1px solid ${lavenderGray[500]}`,
                            color: lavenderGray[500]
                        }
                    },

                    '&.MuiButton-textPrimary': {
                        color: auvikLavender[800],
                        '&&:disabled': {
                            color: lavenderGray[600],
                            cursor: 'not-allowed'
                        }
                    },
                    minWidth: '64px',
                    lineHeight: '1.75',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                    padding: '4px 16px',
                    '&.MuiButton-sizeAnmSmall': {
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'normal',
                        minWidth: '50px',
                        padding: '4px 10px'
                    },
                    '&.MuiButton-sizeAnmMedium': {
                        borderRadius: '4px',
                        fontSize: '13px',
                        minWidth: '170px',
                        padding: '8px 18px'
                        // padding: '4px 16px' // To match current MUI
                    },
                    '&.MuiButton-sizeAnmLarge': {
                        borderRadius: '4px',
                        fontSize: '13px',
                        minWidth: '200px',
                        padding: '12px 18px'
                    }
                })
            }
        },
        MuiButtonBase: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    '&:focus': {
                        outline: 'none'
                    },
                    '&.MuiIconButton-root': {
                        height: '36px',
                        width: '36px',
                        ...(ownerState.variant === 'slim' && {
                            height: '24px',
                            padding: 0,
                            width: '24px'
                        }),
                        // Disabled state has to override hover state
                        '&:disabled': {
                            color: lavenderGray[500],
                            cursor: 'not-allowed'
                        },
                        '&:focus-visible': {
                            backgroundColor: 'rgba(0, 0, 0, 0.15)'
                        },
                        '&:hover': {
                            textDecoration: 'none'
                        },
                        backgroundColor: 'transparent',
                        borderRadius: '50%'
                    },
                    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                    '&.MuiPickersDay-root.Mui-selected': {
                        backgroundColor: auvikPurple[500],
                        color: white,
                        fontWeight: 600,
                        '&:hover, &:focus': {
                            backgroundColor: auvikPurple[500],
                            color: white,
                            fontWeight: 600
                        }
                    },
                    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                    '&.MuiPickersDay-root:not(.Mui-selected)': {
                        borderColor: auvikPurple[500]
                    },
                    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                    '&.MuiPickersDay-root:hover, &.MuiPickersDay-root:focus': {
                        backgroundColor: auvikPurple[500],
                        color: white,
                        fontWeight: 600
                    }
                })
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    textTransform: 'capitalize',
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    textAlign: 'left',
                    '& .MuiTypography-h5': {
                        color: gray[700],
                        fontSize: FontSizes[1]
                    }
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'secondary' && {
                        '&.Mui-checked': {
                            color: auvikPurple[500]
                        }
                    }),
                    ...(ownerState.variant === 'tertiary' && {
                        '&.Mui-checked': {
                            color: auvikLavender[500]
                        }
                    }),
                    ...(ownerState.variant !== 'secondary' &&
                        ownerState.variant !== 'tertiary' && {
                            '&.Mui-checked': {
                                color: auvikEggplant[500]
                            }
                        }),
                    color: lavenderGray[500]
                })
            }
        },
        MuiChip: {
            styleOverrides: {
                label: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'whiteText' && {
                        color: white
                    }),
                    ...(ownerState.variant === 'blackText' && {
                        color: black
                    }),
                    ...(ownerState.variant !== 'whiteText' &&
                        ownerState.variant !== 'blackText' && {
                            color: gray[700]
                        })
                }),
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'secondary' && {
                        backgroundColor: lavenderGray[400]
                    }),
                    ...(ownerState.variant === 'tertiary' && {
                        backgroundColor: auvikPurple[500]
                    }),
                    ...(ownerState.variant === 'rank' && {
                        backgroundColor: lightGray[300]
                    })
                })
            }
        },
        MuiDialog: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                paperFullScreen: {
                    marginLeft: SIDEBAR_WIDTH
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[4]
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                label: {
                    color: gray[700]
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: gray[700],
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                }
            }
        },
        MuiGrid: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: gray[700],
                    '&.MuiInput-underline:before': {
                        borderBottomColor: lavenderGray[500]
                    },
                    '&.MuiInput-underline:after': {
                        borderBottom: `1px solid ${lavenderGray[500]}`
                    },
                    '&.MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                            borderColor: auvikSkyBlue[500],
                            boxShadow:
                                'rgb(0 0 0 / 8%) 0px 1px 1px inset, rgb(102 175 233 / 60%) 0px 0px 8px'
                        }
                    },
                    '&.MuiOutlinedInput-notchedOutline': {
                        borderColor: lavenderGray[500]
                    }
                }
            }
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.color === 'success' && {
                        backgroundColor: eggplantGray[50]
                    }),
                    '&.MuiLinearProgress-colorPrimary': {
                        backgroundColor: eggplantGray[200]
                    }
                }),
                bar: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.color === 'success' && {
                        backgroundColor: auvikGreen[500]
                    }),
                    '&.MuiLinearProgress-barColorPrimary': {
                        backgroundColor: auvikEggplant[500]
                    }
                })
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: auvikLavender[500],
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    '&:hover': {
                        color: auvikPurple[500]
                    }
                }
            }
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                }
            }
        },
        MuiListSubheader: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    backgroundColor: 'unset'
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: gray[700]
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'outlined' && {
                        border: `1px solid ${lavenderGray[500]}`,
                        borderRadius: '4px'
                    }),
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    padding: '8px'
                })
            }
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled:not(.Mui-selected)': {
                        color: gray[300],
                        fontWeight: 300
                    }
                }
            }
        },
        MuiPickersMonth: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: auvikRed[500],
                    '& .MuiPickersMonth-monthButton': {
                        fontSize: FontSizes[1],
                        color: gray[700],
                        '&.Mui-selected': {
                            backgroundColor: auvikPurple[500],
                            '&:hover, &:focus': {
                                backgroundColor: auvikPurple[500],
                                color: white,
                                fontWeight: 600
                            }
                        },
                        '&:hover, &:focus': {
                            backgroundColor: auvikPurple[500],
                            color: white
                        },
                        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                        '&.Mui-disabled:not(.Mui-selected)': {
                            color: gray[300],
                            fontWeight: 300
                        }
                    }
                }
            }
        },
        MuiPickersYear: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: auvikRed[500],
                    '& .MuiPickersYear-yearButton': {
                        fontSize: FontSizes[1],
                        color: gray[700],
                        '&.Mui-selected': {
                            backgroundColor: auvikPurple[500],
                            fontWeight: 600,
                            '&:hover, &:focus': {
                                backgroundColor: auvikPurple[500],
                                color: white,
                                fontWeight: 600
                            }
                        },
                        '&:hover, &:focus': {
                            backgroundColor: auvikPurple[500],
                            color: white
                        },
                        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                        '&.Mui-disabled:not(.Mui-selected)': {
                            color: gray[300],
                            fontWeight: 300
                        }
                    }
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.className === 'asmTenantSelector' && {
                        backgroundColor: eggplantGray[400],
                        border: `1px solid ${auvikLavender[500]}`,
                        borderRadius: '4px',
                        boxSizing: 'border-box',
                        height: '32px',
                        lineHeight: '24px',
                        color: white,
                        width: '100%',
                        '> .MuiSelect-icon': {
                            color: white,
                            height: '22px',
                            width: '33px',
                            ':not(.MuiSelect-iconOpen)': {
                                borderLeft: `1px solid ${lavenderGray[500]}`,
                                paddingLeft: '8px',
                                right: '8px'
                            },
                            '&.MuiSelect-iconOpen': {
                                borderRight: `1px solid ${lavenderGray[500]}`,
                                paddingRight: '8px',
                                right: '8px'
                            }
                        },
                        '> .MuiSelect-standard.MuiInputBase-input.MuiSelect-select.MuiInput-input':
                            {
                                paddingLeft: '9px', // makes the text inside the select align with the sidebar icons below it.
                                paddingRight: '42px' // 24px (standard padding) + 18px (additional padding to keep text from overflowing the chevron area)
                            },
                        ':before': {
                            border: 'none'
                        }
                    })
                })
            }
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    '&.Mui-completed': {
                        color: auvikPurple[500]
                    }
                }
            }
        },
        MuiStepLabel: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: gray[700]
                },
                label: {
                    color: gray[700],
                    '&.Mui-active': {
                        color: gray[700]
                    },
                    '&.Mui-completed': {
                        color: gray[700]
                    }
                }
            }
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(',')
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[2],
                    backgroundColor: eggplantGray[100],
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    marginLeft: '2px',
                    marginRight: '2px',
                    textTransform: 'capitalize',
                    '&.MuiTab-textColorSecondary.Mui-selected': {
                        backgroundColor: white,
                        fontWeight: 600,
                        color: auvikEggplant[500]
                    },
                    '&.MuiTab-textColorSecondary': {
                        color: gray[700]
                    },
                    ...(ownerState.className === 'nestedTabs' && {
                        backgroundColor: eggplantGray[50],
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        marginLeft: 0,
                        marginRight: 0,
                        '&.MuiTab-textColorPrimary.Mui-selected': {
                            fontWeight: 600
                        }
                    })
                }),
                wrapper: {
                    fontSize: FontSizes[2]
                }
            }
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1]
                },
                indicator: ({ ownerState }: { ownerState: OwnerState }) => ({
                    display: 'none',
                    ...(ownerState.className === 'nestedTabs' && {
                        display: 'unset',
                        backgroundColor: auvikEggplant[500],
                        height: '4px'
                    })
                })
            }
        },
        MuiTable: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant === 'alternateRows' && {
                        '& tbody tr:nth-of-type(2n)': {
                            backgroundColor: `${lavenderGray[50]} !important`,
                            '&:hover': {
                                backgroundColor: `${auvikLavender[50]} !important`
                            }
                        }
                    })
                })
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    color: gray[700]
                }
            }
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    overflowY: 'hidden'
                },
                actions: {
                    color: gray[500],
                    '&:hover': {
                        color: auvikPurple[500]
                    }
                },
                caption: {
                    color: gray[700]
                },
                displayedRows: {
                    margin: 0,
                    color: gray[700]
                },
                selectLabel: {
                    margin: 0,
                    color: gray[700]
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: OwnerState }) => ({
                    ...(ownerState.variant !== 'noHover' && {
                        '&:hover': {
                            backgroundColor: auvikLavender[50]
                        }
                    })
                })
            }
        },
        MuiTextField: {
            defaultProps: {
                variant: 'standard'
            },
            styleOverrides: {
                root: {
                    fontFamily: [
                        '"Open Sans"',
                        'Helvetica',
                        'Arial',
                        'Verdana',
                        'sans-serif'
                    ].join(','),
                    fontSize: FontSizes[1],
                    input: {
                        '&::placeholder': {
                            color: lavenderGray[700],
                            opacity: 1
                        }
                    }
                }
            }
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    backgroundColor: white,
                    borderColor: lavenderGray[500],
                    color: gray[700],
                    '&:hover': {
                        backgroundColor: white,
                        color: auvikPurple[500]
                    },
                    '&.Mui-selected': {
                        backgroundColor: white,
                        color: auvikPurple[500]
                    },
                    '&:focus': {
                        borderColor: auvikSkyBlue[500],
                        boxShadow:
                            'rgb(0 0 0 / 8%) 0px 1px 1px inset, rgb(102 175 233 / 60%) 0px 0px 8px'
                    }
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    background: 'none'
                }
            }
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    span: 'span'
                }
            }
        },
        // Dashboard panal styles
        Panel: {
            title: {
                // Variants for the panel title
                large: { fontSize: FontSizes[3], fontWeight: 600 },
                medium: { fontSize: FontSizes[2], fontWeight: 600 },
                small: { fontSize: FontSizes[1], fontWeight: 'normal' }
            }
        }
    }
};
